// Executive Protection details — TanStack Query hooks.
//
// Mirrors the assets.ts hook style: one useQuery per list/detail, one
// useMutation per write, each invalidating the relevant cache keys on
// success. Endpoint shapes match packages/api/src/routes/details.ts:
//
//   /api/details                          protective_details CRUD
//   /api/details/:id/transition           state-machine transition (detail.lead)
//   /api/details/:id/agents               detail_assignments (roster)
//   /api/details/:id/advances             advances (advance.*)
//   /api/details/:id/itineraries          itinerary stops
//   /api/details/:id/legs                 movement_legs (motorcade)
//   /api/details/:id/threat-assessments   threat_assessments (threat.*)
//
// Parent lookups (principal / organization / personnel pickers) are kept
// local to this module; dedicated lookup modules are wired during Integration.

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  ProtectiveDetail,
  ProtectiveDetailsListResponse,
  ProtectiveDetailResponse,
  DetailStatus,
  CreateProtectiveDetailInput,
  UpdateProtectiveDetailInput,
  DetailAssignment,
  DetailAssignmentsListResponse,
  DetailAssignmentResponse,
  CreateDetailAssignmentInput,
  UpdateDetailAssignmentInput,
  Advance,
  AdvancesListResponse,
  AdvanceResponse,
  CreateAdvanceInput,
  UpdateAdvanceInput,
  Itinerary,
  ItinerariesListResponse,
  ItineraryResponse,
  CreateItineraryInput,
  UpdateItineraryInput,
  MovementLeg,
  MovementLegsListResponse,
  MovementLegResponse,
  CreateMovementLegInput,
  UpdateMovementLegInput,
  ThreatAssessment,
  ThreatAssessmentsListResponse,
  ThreatAssessmentResponse,
  CreateThreatAssessmentInput,
  UpdateThreatAssessmentInput,
  PrincipalsListResponse,
  OrganizationsListResponse,
  PersonnelListResponse,
} from '@dsr/shared-types';
import { api } from '../client';

// ---------------------------------------------------------------------------
// protective_details
// ---------------------------------------------------------------------------

export interface DetailFilters {
  status?: DetailStatus;
  principal_id?: number;
  organization_id?: number;
}

export function useDetails(filters: DetailFilters = {}) {
  const params = new URLSearchParams();
  if (filters.status) params.set('status', filters.status);
  if (filters.principal_id !== undefined) {
    params.set('principal_id', String(filters.principal_id));
  }
  if (filters.organization_id !== undefined) {
    params.set('organization_id', String(filters.organization_id));
  }
  const qs = params.toString();
  const url = qs.length > 0 ? `/api/details?${qs}` : '/api/details';
  return useQuery({
    queryKey: ['details', { ...filters }],
    queryFn: ({ signal }) => api.get<ProtectiveDetailsListResponse>(url, signal),
  });
}

export function useDetail(id: number | null) {
  return useQuery({
    queryKey: ['detail', id],
    enabled: id !== null && id > 0,
    queryFn: ({ signal }) =>
      api.get<ProtectiveDetailResponse>(`/api/details/${id}`, signal),
  });
}

export function useCreateDetail() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateProtectiveDetailInput) =>
      api.post<ProtectiveDetailResponse>('/api/details', input),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['details'] });
    },
  });
}

export interface UpdateDetailVars extends UpdateProtectiveDetailInput {
  id: number;
}

export function useUpdateDetail() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...patch }: UpdateDetailVars) =>
      api.patch<ProtectiveDetailResponse>(`/api/details/${id}`, patch),
    onSuccess: (_data, vars) => {
      void qc.invalidateQueries({ queryKey: ['details'] });
      void qc.invalidateQueries({ queryKey: ['detail', vars.id] });
    },
  });
}

// State-machine transition. Gated server-side on detail.lead (separate from
// detail.write), so the page only surfaces this control when the user holds
// that permission.
export function useTransitionDetail() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, status }: { id: number; status: DetailStatus }) =>
      api.post<ProtectiveDetailResponse>(`/api/details/${id}/transition`, { status }),
    onSuccess: (_data, vars) => {
      void qc.invalidateQueries({ queryKey: ['details'] });
      void qc.invalidateQueries({ queryKey: ['detail', vars.id] });
    },
  });
}

export function useDeleteDetail() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => api.delete<void>(`/api/details/${id}`),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['details'] });
    },
  });
}

// ---------------------------------------------------------------------------
// detail_assignments (roster)
// ---------------------------------------------------------------------------

export function useDetailAgents(detailId: number | null) {
  return useQuery({
    queryKey: ['detail-agents', detailId],
    enabled: detailId !== null && detailId > 0,
    queryFn: ({ signal }) =>
      api.get<DetailAssignmentsListResponse>(`/api/details/${detailId}/agents`, signal),
  });
}

export function useCreateDetailAgent() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      detailId,
      ...input
    }: { detailId: number } & CreateDetailAssignmentInput) =>
      api.post<DetailAssignmentResponse>(`/api/details/${detailId}/agents`, input),
    onSuccess: (_data, vars) => {
      void qc.invalidateQueries({ queryKey: ['detail-agents', vars.detailId] });
    },
  });
}

export function useUpdateDetailAgent() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      detailId,
      agentId,
      ...patch
    }: { detailId: number; agentId: number } & UpdateDetailAssignmentInput) =>
      api.patch<DetailAssignmentResponse>(
        `/api/details/${detailId}/agents/${agentId}`,
        patch,
      ),
    onSuccess: (_data, vars) => {
      void qc.invalidateQueries({ queryKey: ['detail-agents', vars.detailId] });
    },
  });
}

export function useDeleteDetailAgent() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ detailId, agentId }: { detailId: number; agentId: number }) =>
      api.delete<void>(`/api/details/${detailId}/agents/${agentId}`),
    onSuccess: (_data, vars) => {
      void qc.invalidateQueries({ queryKey: ['detail-agents', vars.detailId] });
    },
  });
}

// ---------------------------------------------------------------------------
// advances
// ---------------------------------------------------------------------------

export function useAdvances(detailId: number | null) {
  return useQuery({
    queryKey: ['detail-advances', detailId],
    enabled: detailId !== null && detailId > 0,
    queryFn: ({ signal }) =>
      api.get<AdvancesListResponse>(`/api/details/${detailId}/advances`, signal),
  });
}

export function useCreateAdvance() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ detailId, ...input }: { detailId: number } & CreateAdvanceInput) =>
      api.post<AdvanceResponse>(`/api/details/${detailId}/advances`, input),
    onSuccess: (_data, vars) => {
      void qc.invalidateQueries({ queryKey: ['detail-advances', vars.detailId] });
    },
  });
}

export function useUpdateAdvance() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      detailId,
      advanceId,
      ...patch
    }: { detailId: number; advanceId: number } & UpdateAdvanceInput) =>
      api.patch<AdvanceResponse>(
        `/api/details/${detailId}/advances/${advanceId}`,
        patch,
      ),
    onSuccess: (_data, vars) => {
      void qc.invalidateQueries({ queryKey: ['detail-advances', vars.detailId] });
    },
  });
}

export function useDeleteAdvance() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ detailId, advanceId }: { detailId: number; advanceId: number }) =>
      api.delete<void>(`/api/details/${detailId}/advances/${advanceId}`),
    onSuccess: (_data, vars) => {
      void qc.invalidateQueries({ queryKey: ['detail-advances', vars.detailId] });
    },
  });
}

// ---------------------------------------------------------------------------
// itineraries
// ---------------------------------------------------------------------------

export function useItineraries(detailId: number | null) {
  return useQuery({
    queryKey: ['detail-itineraries', detailId],
    enabled: detailId !== null && detailId > 0,
    queryFn: ({ signal }) =>
      api.get<ItinerariesListResponse>(`/api/details/${detailId}/itineraries`, signal),
  });
}

export function useCreateItinerary() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ detailId, ...input }: { detailId: number } & CreateItineraryInput) =>
      api.post<ItineraryResponse>(`/api/details/${detailId}/itineraries`, input),
    onSuccess: (_data, vars) => {
      void qc.invalidateQueries({ queryKey: ['detail-itineraries', vars.detailId] });
    },
  });
}

export function useUpdateItinerary() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      detailId,
      itinId,
      ...patch
    }: { detailId: number; itinId: number } & UpdateItineraryInput) =>
      api.patch<ItineraryResponse>(
        `/api/details/${detailId}/itineraries/${itinId}`,
        patch,
      ),
    onSuccess: (_data, vars) => {
      void qc.invalidateQueries({ queryKey: ['detail-itineraries', vars.detailId] });
    },
  });
}

export function useDeleteItinerary() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ detailId, itinId }: { detailId: number; itinId: number }) =>
      api.delete<void>(`/api/details/${detailId}/itineraries/${itinId}`),
    onSuccess: (_data, vars) => {
      void qc.invalidateQueries({ queryKey: ['detail-itineraries', vars.detailId] });
    },
  });
}

// ---------------------------------------------------------------------------
// movement_legs (motorcade)
// ---------------------------------------------------------------------------

export function useMovementLegs(detailId: number | null) {
  return useQuery({
    queryKey: ['detail-legs', detailId],
    enabled: detailId !== null && detailId > 0,
    queryFn: ({ signal }) =>
      api.get<MovementLegsListResponse>(`/api/details/${detailId}/legs`, signal),
  });
}

export function useCreateMovementLeg() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ detailId, ...input }: { detailId: number } & CreateMovementLegInput) =>
      api.post<MovementLegResponse>(`/api/details/${detailId}/legs`, input),
    onSuccess: (_data, vars) => {
      void qc.invalidateQueries({ queryKey: ['detail-legs', vars.detailId] });
    },
  });
}

export function useUpdateMovementLeg() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      detailId,
      legId,
      ...patch
    }: { detailId: number; legId: number } & UpdateMovementLegInput) =>
      api.patch<MovementLegResponse>(`/api/details/${detailId}/legs/${legId}`, patch),
    onSuccess: (_data, vars) => {
      void qc.invalidateQueries({ queryKey: ['detail-legs', vars.detailId] });
    },
  });
}

export function useDeleteMovementLeg() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ detailId, legId }: { detailId: number; legId: number }) =>
      api.delete<void>(`/api/details/${detailId}/legs/${legId}`),
    onSuccess: (_data, vars) => {
      void qc.invalidateQueries({ queryKey: ['detail-legs', vars.detailId] });
    },
  });
}

// ---------------------------------------------------------------------------
// threat_assessments
// ---------------------------------------------------------------------------

export function useThreatAssessments(detailId: number | null) {
  return useQuery({
    queryKey: ['detail-threats', detailId],
    enabled: detailId !== null && detailId > 0,
    queryFn: ({ signal }) =>
      api.get<ThreatAssessmentsListResponse>(
        `/api/details/${detailId}/threat-assessments`,
        signal,
      ),
  });
}

export function useCreateThreatAssessment() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      detailId,
      ...input
    }: { detailId: number } & CreateThreatAssessmentInput) =>
      api.post<ThreatAssessmentResponse>(
        `/api/details/${detailId}/threat-assessments`,
        input,
      ),
    onSuccess: (_data, vars) => {
      void qc.invalidateQueries({ queryKey: ['detail-threats', vars.detailId] });
    },
  });
}

export function useUpdateThreatAssessment() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      detailId,
      taId,
      ...patch
    }: { detailId: number; taId: number } & UpdateThreatAssessmentInput) =>
      api.patch<ThreatAssessmentResponse>(
        `/api/details/${detailId}/threat-assessments/${taId}`,
        patch,
      ),
    onSuccess: (_data, vars) => {
      void qc.invalidateQueries({ queryKey: ['detail-threats', vars.detailId] });
    },
  });
}

export function useDeleteThreatAssessment() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ detailId, taId }: { detailId: number; taId: number }) =>
      api.delete<void>(`/api/details/${detailId}/threat-assessments/${taId}`),
    onSuccess: (_data, vars) => {
      void qc.invalidateQueries({ queryKey: ['detail-threats', vars.detailId] });
    },
  });
}

// ---------------------------------------------------------------------------
// Parent lookups (principal / organization / personnel pickers + name
// resolution). Cached longer than the default since the option lists change
// rarely. Dedicated lookup modules are wired during Integration.
// ---------------------------------------------------------------------------

export function usePrincipalsLookup() {
  return useQuery({
    queryKey: ['details', 'principals-lookup'],
    queryFn: ({ signal }) =>
      api.get<PrincipalsListResponse>('/api/principals', signal),
    staleTime: 60_000,
  });
}

export function useOrganizationsLookup() {
  return useQuery({
    queryKey: ['details', 'organizations-lookup'],
    queryFn: ({ signal }) =>
      api.get<OrganizationsListResponse>('/api/organizations', signal),
    staleTime: 60_000,
  });
}

export function usePersonnelLookup() {
  return useQuery({
    queryKey: ['details', 'personnel-lookup'],
    queryFn: ({ signal }) =>
      api.get<PersonnelListResponse>('/api/personnel', signal),
    staleTime: 60_000,
  });
}

// Re-export the wire types callers most often need alongside the hooks.
export type {
  ProtectiveDetail,
  DetailAssignment,
  Advance,
  Itinerary,
  MovementLeg,
  ThreatAssessment,
};
